import { useEffect } from 'react'
import { THEME_ENUM } from '@/constants/theme.constant'
import { useThemeStore } from '@/store/themeStore'
import type { Mode } from '@/@types/theme'

function useDarkMode(): [
    isEnabled: boolean,
    onModeChange: (mode: Mode) => void,
] {
    const mode = useThemeStore((state) => state.mode)
    const setMode = useThemeStore((state) => state.setMode)

    const { MODE_DARK, MODE_LIGHT } = THEME_ENUM

    const isEnabled = mode === MODE_DARK

    // Binary light/dark toggles route through setThemeName so the 5-theme
    // variant (dim/forest/sepia) is cleared too — otherwise flipping dark while
    // e.g. sepia is active would leave the sepia class on a dark mode (Handoff
    // 19 §5). setThemeName syncs `mode` itself, so we don't call setMode here.
    const onModeChange = (mode: Mode) => {
        const setThemeName = useThemeStore.getState().setThemeName
        if (setThemeName) {
            setThemeName(mode === MODE_DARK ? 'dark' : 'light')
        } else {
            setMode(mode)
        }
    }

    useEffect(() => {
        if (typeof window === 'undefined') return
        const root = window.document.documentElement
        // Tailwind darkMode: 'class' only needs `.dark` present/absent.
        // Avoid thrashing both light+dark class names on every toggle.
        root.classList.toggle(MODE_DARK, isEnabled)
        root.classList.remove(MODE_LIGHT)
        root.style.colorScheme = isEnabled ? 'dark' : 'light'
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEnabled])

    return [isEnabled, onModeChange]
}

export default useDarkMode
